// ─────────────────────────────────────────────────────────────────────────────
// Builder permettant de configurer massia tabulator remote
// ─────────────────────────────────────────────────────────────────────────────

import { GridConfigDto } from '../adapter/grid-config-adapter.types';
import { MassiaFilter, MassiaPagination, MassiaSort, MassiaTabulatorParamsHelpers } from '../helpers/mapping.tabulator.params.helpers';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Callback métier que chaque contrôleur fournit.
 * Reçoit les paramètres Massia.
 * Retourne la promesse de réponse API brute ({ items, total } ou tableau).
 */
export type MassiaFetchCallback<TResponse = unknown> = (
	filters: MassiaFilter[],
	sorts: MassiaSort[],
	pagination: MassiaPagination
) => Promise<TResponse>;

/**
 * Hook optionnel appelé après chaque chargement réussi,
 * avec la réponse brute. Permet au contrôleur de mettre à jour
 * ses propres variables (ex: vm.factures, vm.totalCount...).
 */
export type MassiaAfterLoadCallback<TResponse = unknown> = (
	response: TResponse,
	filters: MassiaFilter[],
	sorts: MassiaSort[],
	page: number,
	pageSize: number
) => void;

export interface MassiaGridState {
	page?: number;
	size?: number;
	sort?: unknown[];
	filter?: { field: string; value: unknown }[];
}

export interface MassiaDataShareService {
	getGridState(key: string): MassiaGridState | undefined;
	setGridState(key: string, state: MassiaGridState): void;
}

export interface MassiaRemoteConfigOptions<TResponse = unknown> {
	/** La config brute retournée par le back — sera enrichie et retournée */
	serverConfig: GridConfigDto;

	/** Clé DataShare pour persistance page/sort/filtre (ex: 'factures') */
	dataShareKey: string;

	/** Service DataShare AngularJS injecté dans le contrôleur */
	DataShareService: MassiaDataShareService;

	/** Actions qui déclenchent une sauvegarde d'état avant navigation (par exemple pour retour sur grille après consultation : ['edit', visualize]) */
	navigationActions?: string[];

	/** Taille de page par défaut si rien en DataShare (défaut: 200) */
	defaultPageSize?: number;

	/** URL ajax de base (ex: `${__configuration.apiUrl}/massia/factures`) */
	ajaxURL: string;

	/**
	 * Callback métier pur : reçoit filters/sorts/pagination,
	 * retourne la promesse API. Fourni par le controleur metier (ex:factures)
	 */
	fetchFn: MassiaFetchCallback<TResponse>;

	/** Appelé après chaque chargement avec la réponse brute (optionnel) */
	onAfterLoad?: MassiaAfterLoadCallback<TResponse>;

	/**
	 * Callback appelé à chaque appel fetch avec les params Tabulator bruts.
	 * Utile pour conserver une référence aux derniers params (ex: select-all).
	 */
	onParams?: (params: Record<string, unknown>) => void;

	/** Sélecteur CSS du conteneur de pagination (par defaut: '#pagination-container') */
	paginationElement?: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Builder
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Construit la configuration `remote` générique pour MassiaTabulator.
 */
export class MassiaTabulatorRemoteConfigBuilder {
	static build<TResponse = unknown>(options: MassiaRemoteConfigOptions<TResponse>): GridConfigDto {
		const {
			serverConfig,
			ajaxURL,
			dataShareKey,
			DataShareService,
			navigationActions = ['rowClick', 'visualize', 'edit'],
			paginationElement = '#pagination-container',
			defaultPageSize = 200,
			fetchFn,
			onAfterLoad,
			onParams
		} = options;

		// Lire l'état sauvegardé (page, taille, tri, filtres)
		const savedState = DataShareService.getGridState(dataShareKey) || {};
		const pageSize = savedState.size || defaultPageSize;
		const page = savedState.page || 1;
		const sort = savedState.sort || [];
		const filter = (savedState.filter || []).map((f: { field: string; value: unknown }) => ({ field: f.field, value: f.value }));

		//on retourne la configuration remote pour tabulator
		const remoteConfig = {
			enabled: true,
			ajaxURL,
			pageSize,
			page,
			sort,
			// Tabulator initialHeaderFilter attend [{field, value}] (filtre de la grille)
			filter,
			element: paginationElement,
			fetchFn: (url: string, config: RequestInit | Record<string, unknown>, params: Record<string, unknown>) => {
				// Exposer les params bruts au contrôleur si besoin (ex: select-all)
				if (onParams) {
					onParams(params);
				}

				// Traduire les params Tabulator → format Massia
				const { filters, sorts, pagination } = MassiaTabulatorParamsHelpers.buildMassiaRequestParams(params);

				// Appeler le callback métier pur du contrôleur
				return fetchFn(filters, sorts, pagination).then((response: TResponse) => {
					// Hook post-chargement (mise à jour variables locales du contrôleur)
					if (onAfterLoad) {
						onAfterLoad(response, filters, sorts, page, pageSize);
					}

					// Formater la réponse pour Tabulator
					return MassiaTabulatorParamsHelpers.buildTabulatorResponse(
						response as unknown[] | Record<string, unknown>,
						(params.size as number) ?? pageSize
					);
				});
			}
		};

		// Construire la config persistence
		const persistence = {
			enabled: false,
			id: dataShareKey,
			dataShareKey,
			navigationActions,
			// closure qui capture DataShareService
			// utilisé par le contrôleur Tabulator pour sauvegarder l'état de la grille
			onSaveGridState: (getState: () => MassiaGridState) => {
				DataShareService.setGridState(dataShareKey, getState());
			}
		};

		//Fusionner dans le serverConfig du back (sans le muter directement)
		return {
			...serverConfig,
			features: {
				...serverConfig.features,
				remote: remoteConfig,
				persistence
			}
		};
	}
}
